import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

/**
 * useDashboardStats - Calculates portfolio completion statistics
 * Mirrors the portfolio builder logic for exact parity.
 */
export const useDashboardStats = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    portfolioProgress: 0,
    completedSections: 0,
    totalSections: 6,
    uploadedDocuments: 0,
    missingElements: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, [user?.id]);

  const loadStats = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Fetch personal info (same table used by portfolio builder)
      const { data: personal } = await supabase
        .from('personal_info')
        .select('first_name, last_name, email')
        .eq('user_id', user.id)
        .single();

      const personalRequired = ['first_name', 'last_name', 'email'];
      const completedPersonal = personalRequired.filter((f) => personal?.[f]?.trim()).length;
      const personalComplete = personalRequired.length > 0 && completedPersonal === personalRequired.length;

      // Fetch documents from portfolio_documents (same as portfolio builder)
      const { data: docsData } = await supabase
        .from('portfolio_documents')
        .select('id, category')
        .eq('user_id', user.id);

      const docBuckets = {
        academic: 0,
        achievements: 0,
        volunteering: 0,
        skills: 0,
        extracurricular: 0,
      };

      docsData?.forEach((doc) => {
        const key = doc.category;
        if (key && docBuckets[key] !== undefined) {
          docBuckets[key] += 1;
        }
      });

      // Sections and completion rules (identical to portfolio page)
      const sections = [
        { key: 'personal', label: 'Personal Information', isComplete: personalComplete },
        { key: 'academic', label: 'Academic Records', isComplete: docBuckets.academic >= 2 },
        { key: 'achievements', label: 'Personal Projects', isComplete: docBuckets.achievements >= 1 },
        { key: 'volunteering', label: 'Volunteering Experience', isComplete: docBuckets.volunteering >= 1 },
        { key: 'skills', label: 'Skills & Languages', isComplete: docBuckets.skills >= 1 },
        { key: 'extracurricular', label: 'Extracurricular Activities', isComplete: docBuckets.extracurricular >= 1 },
      ];

      const completedCount = sections.filter((s) => s.isComplete).length;
      const progressPercent = Math.round((completedCount / sections.length) * 100);
      const missingElements = sections
        .filter((s) => !s.isComplete)
        .map((s) => ({
          label: s.label,
          icon: getIconForSection(s.key),
          sectionKey: s.key,
        }));

      setStats({
        portfolioProgress: progressPercent,
        completedSections: completedCount,
        totalSections: sections.length,
        uploadedDocuments: docsData?.length || 0,
        missingElements,
      });
    } catch (err) {
      console.error('Error loading dashboard stats:', err);
      setStats((prev) => ({ ...prev, portfolioProgress: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const getIconForSection = (key) => {
    const iconMap = {
      personal: 'User',
      academic: 'FileText',
      achievements: 'Briefcase',
      skills: 'Sparkles',
      extracurricular: 'Award',
      volunteering: 'Heart',
    };
    return iconMap[key] || 'File';
  };

  return { ...stats, loading };
};

export default useDashboardStats;
